import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  IIdValuePairResponse,
  ICnicVerification,
  ICnicVerificationListProp,
} from "interfaces/Index";
import DataTable from "components/common/DataTable/DataTable";
import {
  headerData,
  footerData,
  bodyDataAndKeyTypes,
} from "./CnicVerificationTableContants";
import { _ } from "utils/lib";
import { Input, InputGroup, InputGroupAddon, InputGroupText } from "reactstrap";
import Toaster from "components/common/Toaster/Toaster";
import LoaderComponent from "components/common/Loader/Loader";
import { Can } from "config/Can";
import "./CnicVerificationList.scss";
import IconSearch from "assets/images/search-icon.svg";
import IconClose from "assets/images/close.svg";
import {
  CNIC_LISTNG_FILTER,
  CNIC_VERIFICATION_FILTER,
  CUSTOMER_CHANNEL,
} from "config/constants";
import { normalizeCnicData } from "normalizer/CnicVerificationNormalizer";
import SearchableSelect from "components/common/SearchableSelect/SearchableSelect";
import { CNIC_STATUS } from "config/constants";

export const doAPIsearch = (
  val: any,
  pageScrollPosition: any,
  fetchCnicVerificationData: any,
  selectedCustomerChannel: any
) => {
  if (val.length >= 3) {
    pageScrollPosition.current = 0;
    fetchCnicVerificationData({
      searchKey: val,
      status: CNIC_STATUS.PENDING,
      customerChannel: selectedCustomerChannel.id
    });
  }
};

const CnicVerificationList: React.FunctionComponent<
  ICnicVerificationListProp
> = (props: ICnicVerificationListProp) => {
  const {
    cnicVerificationData,
    isCnicVerified,
    error,
    isError,
    isCnicVerificationDataFetched,
    changeListingFilter,
    addUpdatePageHandler,
    updateCount,
    citiesList,
    selectedCity,
    selectedCustomerChannel,
    fetchCnicVerificationData,
    // pageScrollPosition,
  } = props;
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  let pageScrollPosition: any = useRef(props.currentListPosition);

  const [isSearch, setIsSearching] = useState(
    props.retainedSearchText ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(props.retainedSearchText);
  const [cnicVerification, setCnicVerification] = useState<ICnicVerification[]>(
    []
  );

  useEffect(() => {
    if (cnicVerificationData) {
      const filteredRecords = filterRecords(props.listingFilter);
      setCnicVerification(filteredRecords);
      updateCount(filteredRecords.length);
    }
  }, [props.cnicVerificationData]);

  useEffect(() => {
    if (isError && error) {
      const message = (error! as any).errors;
      handleToaster(true, message);
    }
  }, [props.isError]);

  useEffect(() => {
    changeFilterSelection(
      getCustomerChannelList()[0],
      CNIC_VERIFICATION_FILTER.CUSTOMER_CHANNEL
    );
  }, []);

  useEffect(() => {
    if (isCnicVerified) {
      const message = "Updated CNIC successfully!";
      handleToaster(false, message);
    }
  }, [props.isCnicVerified]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const closeSearch = () => {
    pageScrollPosition.current = 0;
    fetchCnicVerificationData();
    setIsSearching(false);
    setSearchTerm("");
  };

  const filterRecords = (filerSelection: IIdValuePairResponse) => {
    let filteredRecords: ICnicVerification[] = [];
    if (filerSelection.id === "credit") {
      filteredRecords = cnicVerificationData.filter(
        (cnicData) => cnicData.userImageId !== ""
      );
    } else {
      filteredRecords = cnicVerificationData.filter(
        (cnicData) => cnicData.userImageId === ""
      );
    }
    return filteredRecords;
  };

  const debounceSearch = 
    _.debounce(
      (value: any) =>
        doAPIsearch(
          value,
          pageScrollPosition,
          props.fetchCnicVerificationData,
          selectedCustomerChannel
        ),
      1000
    )

  const handleSearchInputChange = (e: any) => {
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value);
    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      fetchCnicVerificationData({
        status: CNIC_STATUS.PENDING,
      });
    }
  };

  const getCustomerChannelList = () => {
    let channelList = [];
    for (let item in CUSTOMER_CHANNEL) {
      channelList.push({
        id: item,
        name: item,
        value: item,
        label: item.split("_").join(" "),
      });
    }
    return channelList;
  };

  const changeFilterSelection = (
    filerSelection: IIdValuePairResponse,
    filterType: string
  ) => {
    changeListingFilter(filerSelection, filterType);

    const filteredRecords = filterRecords(filerSelection);

    setCnicVerification(filteredRecords);
    updateCount(filteredRecords.length);
  };

  const editCnicVerificationData = (e: any, i: number) => {
    addUpdatePageHandler(true, cnicVerification[i]);
  };

  return (
    <div className="cnic-verification-list-container">
      {!isCnicVerificationDataFetched && <LoaderComponent />}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <section className="relative container sticky-top">
        <section className="row">
          <section className="col-12">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <img src={IconSearch} alt="close" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className="search-input"
                placeholder="Search by user id or CNIC number"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
              <div className="input-group-append extra-filter">
                <SearchableSelect
                  id="customer-channel-filter"
                  isSearchable={true}
                  options={getCustomerChannelList()}
                  onChange={(filterSelection) => {
                    changeFilterSelection(
                      filterSelection,
                      CNIC_VERIFICATION_FILTER.CUSTOMER_CHANNEL
                    );
                  }}
                  selectedValue={selectedCustomerChannel}
                />
              </div>

              <div className="input-group-append extra-filter">
                <SearchableSelect
                  id="cnic-filter"
                  isSearchable={true}
                  options={citiesList}
                  onChange={(filterSelection) => {
                    changeFilterSelection(
                      filterSelection,
                      CNIC_VERIFICATION_FILTER.CITIES
                    );
                  }}
                  selectedValue={selectedCity}
                />
              </div>
              <div className="input-group-append">
                <SearchableSelect
                  id="cnic-filter"
                  isSearchable={true}
                  options={CNIC_LISTNG_FILTER}
                  onChange={(filterSelection) => {
                    changeFilterSelection(
                      filterSelection,
                      CNIC_VERIFICATION_FILTER.LISTING
                    );
                  }}
                  selectedValue={props.listingFilter}
                />
              </div>
              <div
                className={isSearch ? "close-button" : "hide close-button"}
                onClick={closeSearch}
              >
                <img src={IconClose} alt="close" />
              </div>
            </InputGroup>
          </section>
        </section>
      </section>
      {/* <Can I="read" a="nationalid"> */}
        <DataTable
          tableId="cnic-verification-list"
          tableHeaderData={headerData}
          tableFooterData={footerData}
          tableBodyDataTypes={bodyDataAndKeyTypes}
          tableBodyRowData={normalizeCnicData(cnicVerification)}
          noSearchResultFlag={true}
        editRowHandler={editCnicVerificationData}
        />
      {/* </Can> */}
    </div>
  );
};

export default CnicVerificationList;
